import { type FC, useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Activity, Plus, X } from "lucide-react";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import { useNavigate } from "react-router-dom";
import SessionStorageHelper from "../../utils/sessionStorageHelper";

interface MedicationsData {
  medications: Array<{
    name: string;
    doses?: Array<{
      frequency: string;
      time: string;
    }>;
    // Legacy fields for backward compatibility
    dose?: string;
    frequency?: string;
    time?: string;
  }>;
}

interface CurrentMedicationsProps {
  onSubmit?: (data: MedicationsData) => void;
}

const CurrentMedications: FC<CurrentMedicationsProps> = () => {
  const navigate = useNavigate();
  const [medications, setMedications] = useState<any[]>([]);

  // Remove medication function
  const removeMedication = (medicationId: string | number) => {
    const updatedMedications = medications.filter(
      med => med.id !== medicationId
    );
    setMedications(updatedMedications);
    localStorage.setItem("userMedications", JSON.stringify(updatedMedications));
  };

  const handleNext = () => {
    // Separate medications by type - Clean format
    const medicineData = medications
      .filter(med => med.type === "medicine" || !med.type) // Handle both new and legacy data
      .map(med => {
        if (med.doses && Array.isArray(med.doses)) {
          // Clean structure - only name and doses array
          return {
            name: med.name,
            doses: med.doses.map((dose: any) => ({
              frequency: dose.frequency,
              time: dose.time,
            })),
          };
        } else {
          // Legacy structure converted to clean format
          return {
            name: med.name,
            doses: [
              {
                frequency: med.frequency || "daily",
                time: med.time || "08:00",
              },
            ],
          };
        }
      });

    const insulinData = medications
      .filter(med => med.type === "insulin")
      .map(med => ({
        dose_type: med.doseType,
        insulin_type: med.insulinType,
        timing: med.timing,
        dose: med.dose,
        brand: med.brand,
      }));

    // Save to separate fields as expected by backend
    SessionStorageHelper.saveMultipleFields({
      medication_details: medicineData,
      insulin_details: insulinData,
    });

    // Navigate to the next assessment page (weight loss page)
    navigate("/glucera" + GluceraNavigationRoutes.WEIGHT_LOSS);
  };

  // Load medications from localStorage on component mount and when page becomes visible
  useEffect(() => {
    const loadMedications = () => {
      const savedMedications = JSON.parse(
        localStorage.getItem("userMedications") ?? "[]"
      );
      setMedications(savedMedications);
    };

    // Load initially
    loadMedications();

    // Listen for storage changes (when data is updated from another component)
    window.addEventListener("storage", loadMedications);

    // Listen for focus (when returning to this page)
    window.addEventListener("focus", loadMedications);

    return () => {
      window.removeEventListener("storage", loadMedications);
      window.removeEventListener("focus", loadMedications);
    };
  }, []);

  // Also reload when component mounts (for navigation back)
  useEffect(() => {
    const savedMedications = JSON.parse(
      localStorage.getItem("userMedications") ?? "[]"
    );
    setMedications(savedMedications);
  }, [navigate]);

  return (
    <div className="h-screen w-screen overflow-hidden bg-background">
      <div className="mx-auto flex h-full w-full max-w-md flex-col">
        {/* Header */}
        <div className="space-y-4 p-4">
          {/* Logo */}
          <div className="flex items-center justify-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-orange-400">
              <Activity className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-slate-500">
              Bite Balance
            </span>
          </div>

          {/* Assessment Progress */}
          <p className="text-center text-sm text-muted-foreground">
            Assessment 6 of 12
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="mb-6 space-y-2">
            <h1 className="text-3xl font-bold text-foreground sm:text-4xl">
              Your Current Medications
            </h1>
          </div>

          {/* Display Saved Medications */}
          {medications.length > 0 && (
            <div className="mb-4 space-y-3">
              {medications.map(medication => (
                <Card
                  key={medication.id}
                  className="border-2 border-border bg-card p-4"
                >
                  {medication.type === "medicine" ? (
                    <>
                      <div className="mb-3 flex items-start justify-between">
                        <div className="mb-1 text-lg font-semibold text-foreground">
                          {medication.name}
                        </div>
                        <button
                          onClick={() => removeMedication(medication.id)}
                          className="flex h-6 w-6 items-center justify-center rounded-full text-red-500 transition-colors hover:bg-red-50 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>

                      {medication.doses && medication.doses.length > 0 ? (
                        <div className="space-y-3">
                          {medication.doses.map((dose: any) => (
                            <div
                              key={dose.id}
                              className="rounded-lg bg-muted/20 p-3"
                            >
                              <div className="mb-2 flex items-center justify-between">
                                <h4 className="font-medium text-foreground">
                                  {dose.name ||
                                    `Dose ${medication.doses.indexOf(dose) + 1}`}
                                </h4>
                                <div className="rounded-full bg-primary px-2 py-1 text-xs font-medium text-primary-foreground">
                                  {dose.time}
                                </div>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                <span className="font-medium">Frequency:</span>{" "}
                                {dose.frequency}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        // Legacy display for backward compatibility
                        <>
                          <div className="mb-3 flex items-center justify-between">
                            <div>
                              <div className="mb-1 text-sm font-semibold text-foreground">
                                Time
                              </div>
                              <div className="text-muted-foreground">
                                {medication.time}
                              </div>
                            </div>
                            <div className="rounded-full bg-primary px-3 py-1 text-sm font-medium text-primary-foreground">
                              {medication.frequency}
                            </div>
                          </div>
                          <div className="mb-3 flex justify-between">
                            <div>
                              <div className="mb-1 text-sm font-semibold text-foreground">
                                Dose
                              </div>
                              <div className="text-muted-foreground">
                                {medication.dose}
                              </div>
                            </div>
                            <div>
                              <div className="mb-1 text-sm font-semibold text-foreground">
                                Quantity
                              </div>
                              <div className="text-muted-foreground">
                                {medication.quantity}
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="mb-3 flex items-start justify-between">
                        <div className="flex-1">
                          <div className="mb-3 flex items-center justify-between">
                            <div>
                              <div className="mb-1 text-sm font-semibold text-foreground">
                                Time
                              </div>
                              <div className="text-muted-foreground">
                                {medication.timing}
                              </div>
                            </div>
                            <div className="rounded-full bg-primary px-3 py-1 text-sm font-medium text-primary-foreground">
                              {medication.doseType}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => removeMedication(medication.id)}
                          className="ml-2 flex h-6 w-6 items-center justify-center rounded-full text-red-500 transition-colors hover:bg-red-50 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mb-3">
                        <div className="mb-1 text-sm font-semibold text-foreground">
                          Dose
                        </div>
                        <div className="text-muted-foreground">
                          {medication.dose}
                        </div>
                      </div>
                      <div className="mb-3">
                        <div className="mb-1 text-sm font-semibold text-foreground">
                          Insulin Type
                        </div>
                        <div className="text-muted-foreground">
                          {medication.insulinType}
                        </div>
                      </div>
                      <div>
                        <div className="mb-1 text-sm font-semibold text-foreground">
                          Brand
                        </div>
                        <div className="text-muted-foreground">
                          {medication.brand}
                        </div>
                      </div>
                    </>
                  )}
                </Card>
              ))}
            </div>
          )}

          {/* Add Medication Button */}
          <button
            className="flex w-full items-center gap-3 rounded-xl border-2 border-input bg-card px-4 py-3.5 text-foreground transition-all hover:border-primary hover:bg-card/80"
            onClick={() =>
              navigate("/glucera" + GluceraNavigationRoutes.ADD_MEDICATIONS)
            }
          >
            <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-foreground">
              <Plus className="h-4 w-4" />
            </div>
            <span className="font-medium">Add Medication</span>
          </button>
        </div>

        {/* Footer Button */}
        <div className="pb-safe border-t p-4">
          <Button
            onClick={handleNext}
            className="h-11 w-full rounded-xl bg-gradient-primary text-sm hover:opacity-90 sm:h-12 sm:text-base"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CurrentMedications;
